/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Colors, Link, Typography, useTheme } from '@zapier/design-system';
import {
  FacebookIcon,
  LinkedInIcon,
  TwitterIcon,
  YouTubeIcon,
  RssIcon,
  RssIconRebrand,
} from './icons';
import type { SocialIconType } from './icons';

type Props = {
  /**
   * Text to render before the icons.
   */
  title: string;
  /**
   * The social icons to render.
   */
  icons: Array<SocialIconType>;
};

const Styles = {
  root: css`
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  `,
  cell: css`
    flex-grow: 1;
    flex-shrink: 0;
    text-align: center;
  `,
  title: (isRebrand: boolean) => css`
    ${isRebrand ? Typography.smallPrint1Semibold : Typography.smallPrint1};
    color: ${isRebrand ? Colors.neutral700 : Colors.gray60};
    white-space: nowrap;
  `,
  icons: css`
    display: flex;
    width: min-content;
    margin: 0 auto;
  `,
  iconItem: css`
    margin-right: 10px;
    &:last-of-type {
      margin-right: 0;
    }
  `,
  icon: (icon: SocialIconType, isRebrand: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 100%;
    background-color: ${isRebrand ? Colors.neutral500 : Colors.gray60};

    &:hover,
    &:focus {
      background-color: ${icon.color};
      outline: none;
    }
  `,
  iconSvg: (isRebrand: boolean) => css`
    width: 16px;
    height: 16px;
    fill: ${isRebrand ? Colors.neutral100 : Colors.white};

    > svg {
      width: inherit;
      height: inherit;
    }
  `,
};

const SocialIcon = (props: SocialIconType) => {
  const { isRebrand } = useTheme();

  return (
    <Link
      ariaLabel={props.title}
      title={props.title}
      color={null}
      css={Styles.icon(props, isRebrand)}
      data-testid={props.title}
      href={props.href}
    >
      <span
        css={Styles.iconSvg(isRebrand)}
        dangerouslySetInnerHTML={{ __html: props.svg }}
      />
    </Link>
  );
};

/**
 * @private
 * Social icons that link to our social media accounts.
 */
export const SocialIcons = (props: Props) => {
  const { isRebrand } = useTheme();

  const icons = isRebrand
    ? [FacebookIcon, LinkedInIcon, RssIconRebrand, TwitterIcon, YouTubeIcon]
    : props.icons;

  const iconNodes = icons.map((icon: SocialIconType) => {
    return (
      <li key={icon.title} css={Styles.iconItem}>
        <SocialIcon {...icon} />
      </li>
    );
  });

  return (
    <div css={Styles.root}>
      <div css={Styles.cell}>
        <h2 css={Styles.title(isRebrand)}>
          {isRebrand ? 'Follow us' : props.title}
        </h2>
      </div>
      <div css={Styles.cell}>
        <ul css={Styles.icons}>{iconNodes}</ul>
      </div>
    </div>
  );
};

SocialIcons.defaultProps = {
  title: 'Follow us',
  icons: [FacebookIcon, LinkedInIcon, TwitterIcon, YouTubeIcon, RssIcon],
};
